import { fabric } from "fabric";
import { ZoomConfig } from "../types";
import { getCurrentZoomConfig, isZoomModifierPressed } from "../utils/platform";
import { VIEWPORT_ZOOM_LIMITS, WHEEL_DELTA_THRESHOLD } from "../config";

/**
 * =====================================================
 * 滚轮缩放模块
 * =====================================================
 * 功能：
 * - Mac: Cmd + 滚轮 缩放
 * - Windows/Linux: Ctrl + 滚轮 缩放
 * - 自动区分鼠标滚轮和触摸板，使用不同灵敏度
 */

/**
 * 计算新的缩放值
 * @param currentZoom 当前缩放值
 * @param delta 滚轮增量
 * @param config 缩放配置
 * @returns 新的缩放值
 */
export const calculateNewZoom = (
  currentZoom: number,
  delta: number,
  config: ZoomConfig
): number => {
  let zoom = currentZoom;

  // 区分鼠标滚轮和触摸板
  // 鼠标滚轮通常 deltaY 绝对值较大 (如 100)，触摸板较小
  if (Math.abs(delta) > WHEEL_DELTA_THRESHOLD) {
    // 鼠标滚轮
    zoom *= config.mouseWheelSensitivity ** delta;
  } else {
    // 触摸板
    zoom *= config.trackpadSensitivity ** delta;
  }

  // 限制缩放范围
  return Math.max(
    VIEWPORT_ZOOM_LIMITS.MIN_ZOOM,
    Math.min(VIEWPORT_ZOOM_LIMITS.MAX_ZOOM, zoom)
  );
};

/**
 * 处理滚轮缩放
 * @param canvas Fabric 画布实例
 * @param evt 滚轮事件
 * @returns 是否已处理缩放（true=已处理，false=未触发缩放）
 */
export const handleWheelZoom = (
  canvas: fabric.Canvas,
  evt: WheelEvent
): boolean => {
  // 检查是否按下了缩放修饰键
  if (!isZoomModifierPressed(evt)) {
    return false; // 未触发缩放，返回 false 表示继续处理其他逻辑
  }

  const config = getCurrentZoomConfig();
  const delta = evt.deltaY;
  const currentZoom = canvas.getZoom();
  const newZoom = calculateNewZoom(currentZoom, delta, config);

  canvas.zoomToPoint({ x: evt.offsetX, y: evt.offsetY }, newZoom);
  // 触发 viewport:transform 事件，由 SmartCanvas 统一处理坐标同步
  canvas.fire("viewport:transform", { e: evt });

  return true; // 已处理缩放
};

/**
 * 处理触摸板/滚轮平移（非缩放模式时）
 * @param canvas Fabric 画布实例
 * @param evt 滚轮事件
 */
export const handleWheelPan = (canvas: fabric.Canvas, evt: WheelEvent): void => {
  const vpt = canvas.viewportTransform;
  if (vpt) {
    // 使用 setViewportTransform 而非直接修改，确保 Fabric 内部 _offset 等状态正确更新
    // 直接改 vpt[4]/[5] 会导致 getPointer/findTarget 使用的坐标偏移未更新，点击检测不准
    const newVpt = [...vpt];
    newVpt[4] -= evt.deltaX;
    newVpt[5] -= evt.deltaY;
    canvas.setViewportTransform(newVpt);
    canvas.requestRenderAll();
    canvas.fire("viewport:transform", { e: evt });
  }
};
